import { memo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRightIcon, LinesIcon } from "../modal/deviceModalIcons";
import {
  DeviceSectionCard,
  DeviceSectionHeader,
  DeviceSwitchTrack,
} from "../modal/deviceModalParts";

interface Props {
  summarizeReceipt: boolean;
  onSummarizeReceiptChange: (checked: boolean) => void;
  nonFiscalCopy: boolean;
  onNonFiscalCopyChange: (checked: boolean) => void;
  isNarrow: boolean;
}

interface ExampleLine {
  nameKey: string;
  quantity: number;
  price: number;
}

// Една и съща сметка в двата вида бон — сумата е еднаква, мени се само броят
// редове. „Кафе + мляко" е кафе с добавка: различно е от голото кафе и
// остава на свой ред и при включено обобщаване.
const LINES_OFF: ExampleLine[] = [
  { nameKey: "devices.summarizeReceiptExampleWater", quantity: 1, price: 2 },
  { nameKey: "devices.summarizeReceiptExampleCola", quantity: 1, price: 3 },
  { nameKey: "devices.summarizeReceiptExampleWater", quantity: 1, price: 2 },
  { nameKey: "devices.summarizeReceiptExampleCoffee", quantity: 1, price: 2.5 },
  { nameKey: "devices.summarizeReceiptExampleCoffeeMilk", quantity: 1, price: 3 },
];
const LINES_ON: ExampleLine[] = [
  { nameKey: "devices.summarizeReceiptExampleWater", quantity: 2, price: 2 },
  { nameKey: "devices.summarizeReceiptExampleCola", quantity: 1, price: 3 },
  { nameKey: "devices.summarizeReceiptExampleCoffee", quantity: 1, price: 2.5 },
  { nameKey: "devices.summarizeReceiptExampleCoffeeMilk", quantity: 1, price: 3 },
];

// Ред „заглавие + обяснение + превключвател" — целият ред е бутонът.
const ToggleRow = ({
  title,
  hint,
  checked,
  onChange,
}: {
  title: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="flex w-full cursor-pointer items-start gap-3 text-left"
  >
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="text-[13px] font-bold leading-[1.3] text-ink">{title}</span>
      <span className="text-[11.5px] leading-[1.4] text-body [text-wrap:pretty]">{hint}</span>
    </span>
    <DeviceSwitchTrack checked={checked} size="lg" />
  </button>
);

// Мини касов бон — активният вид е в акцентен цвят, другият е приглушен.
// Клик избира този вид (както досега).
const ExampleReceipt = ({
  titleKey,
  lines,
  isActive,
  onSelect,
}: {
  titleKey: string;
  lines: ExampleLine[];
  isActive: boolean;
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const total = lines.reduce((sum, line) => sum + line.quantity * line.price, 0);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isActive}
      className={`flex min-w-0 cursor-pointer flex-col gap-[2px] rounded-[10px] border p-2 text-left font-mono text-[10.5px] leading-[1.4] ${
        isActive
          ? "border-brand-tint2 bg-brand-tint text-ink"
          : "border-shade-3 bg-surface text-muted hover:border-brand-tint2"
      }`}
    >
      <span
        className={`mb-0.5 flex items-center justify-between gap-1 font-sans text-[9.5px] font-bold uppercase tracking-[0.6px] ${
          isActive ? "text-brand-deep-ink" : "text-muted"
        }`}
      >
        <span>{t(titleKey)}</span>
        {isActive && (
          <span className="rounded-full bg-brand px-[7px] py-px normal-case tracking-[0.3px] text-white">
            {t("devices.summarizeReceiptActive")}
          </span>
        )}
      </span>
      {lines.map((line, index) => (
        <span key={index} className="flex justify-between gap-2">
          <span className="truncate">{t(line.nameKey)}</span>
          <span className="shrink-0 tabular-nums">
            {line.quantity} × {line.price.toFixed(2)}
          </span>
        </span>
      ))}
      <span className="mt-auto flex justify-between border-t border-dashed border-shade-5 pt-1">
        <span>{t("devices.summarizeReceiptLines", { count: lines.length })}</span>
        <span className="font-semibold tabular-nums">{total.toFixed(2)} €</span>
      </span>
    </button>
  );
};

/**
 * Секция „Касова бележка" — „Обобщени касови бележки" (с пример изключено →
 * включено) и „Нефискално копие на ПОС". Кое се слива и кога копието не
 * излиза решава BE (`fiscal_receipt_summary.py`, `non_fiscal_copy.py`).
 */
const FiscalReceiptSection = ({
  summarizeReceipt,
  onSummarizeReceiptChange,
  nonFiscalCopy,
  onNonFiscalCopyChange,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();
  const blockPadding = isNarrow ? "p-4" : "px-4 py-3";
  return (
    <DeviceSectionCard>
      <DeviceSectionHeader
        icon={<LinesIcon size={18} />}
        iconClassName="bg-fill-2 text-body"
        title={t("devices.fiscalModal.receiptTitle")}
        shortDescription={t("devices.fiscalModal.receiptDescShort")}
        isNarrow={isNarrow}
      />

      <div className={`flex flex-col gap-2.5 border-b border-shade-2 ${blockPadding}`}>
        <ToggleRow
          title={t("devices.summarizeReceipt")}
          hint={t("devices.summarizeReceiptHint")}
          checked={summarizeReceipt}
          onChange={onSummarizeReceiptChange}
        />
        <div
          className={`grid items-stretch gap-2 ${
            isNarrow ? "grid-cols-1" : "grid-cols-[minmax(0,1fr)_12px_minmax(0,1fr)]"
          }`}
        >
          <ExampleReceipt
            titleKey="devices.summarizeReceiptOff"
            lines={LINES_OFF}
            isActive={!summarizeReceipt}
            onSelect={() => onSummarizeReceiptChange(false)}
          />
          {!isNarrow && <ArrowRightIcon size={12} className="self-center text-muted" />}
          <ExampleReceipt
            titleKey="devices.summarizeReceiptOn"
            lines={LINES_ON}
            isActive={summarizeReceipt}
            onSelect={() => onSummarizeReceiptChange(true)}
          />
        </div>
      </div>

      <div className={blockPadding}>
        <ToggleRow
          title={t("devices.nonFiscalCopy")}
          hint={t("devices.nonFiscalCopyHint")}
          checked={nonFiscalCopy}
          onChange={onNonFiscalCopyChange}
        />
      </div>
    </DeviceSectionCard>
  );
};

// memo: при писане на името в хедъра секцията не се прерисува.
export default memo(FiscalReceiptSection);
